import { RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react'

import { getAuthClient, isBetterAuthEnabled } from './client'
import { AuthProvider, readAuthSelection } from './selection'
import { asError, createSessionStore, PortalAuthUser } from './sessionStore'

export type { PortalAuthUser } from './sessionStore'

type PortalAuth = {
  provider: AuthProvider | null
  user?: PortalAuthUser
  isAuthenticated: boolean
  isLoading: boolean
  supportsAuth0AccountManagement: boolean
  error?: Error
  getToken: () => Promise<string>
  logout: () => Promise<void>
  refreshSession: () => Promise<boolean>
  selectProvider: (provider: AuthProvider) => void
  login: (options: RedirectLoginOptions) => Promise<void>
}

const AuthContext = createContext<PortalAuth | null>(null)

export function needsEmailVerification(
  auth: Pick<
    PortalAuth,
    'provider' | 'isAuthenticated' | 'supportsAuth0AccountManagement' | 'user'
  >,
) {
  return (
    auth.provider === 'better-auth' &&
    auth.isAuthenticated &&
    !auth.supportsAuth0AccountManagement &&
    auth.user?.email_verified !== true
  )
}

export function PortalAuthProvider({ children }: PropsWithChildren) {
  const auth0 = useAuth0()
  const [store] = useState(() => {
    if (!isBetterAuthEnabled()) return createSessionStore('auth0')
    const selection = readAuthSelection()
    if (selection) return createSessionStore(selection)
    if (auth0.isLoading) return createSessionStore('unknown')
    return createSessionStore(auth0.isAuthenticated ? 'auth0' : 'better-auth')
  })
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot)
  const isAuth0 = state.status === 'auth0'
  const provider =
    state.status === 'auth0' || state.status === 'better-auth'
      ? state.status
      : null

  useEffect(() => {
    if (state.status === 'unknown' && !auth0.isLoading) {
      store.select(auth0.isAuthenticated ? 'auth0' : 'better-auth')
    }
  }, [auth0.isAuthenticated, auth0.isLoading, state.status, store])

  useEffect(() => {
    if (state.status !== 'better-auth') return
    const refresh = () => {
      void store.refresh().catch(() => {})
    }
    refresh()
    window.addEventListener('focus', refresh)
    return () => {
      store.cancel()
      window.removeEventListener('focus', refresh)
    }
  }, [state.status, store])

  useEffect(() => {
    if (state.status !== 'better-auth' || !state.expiresAt) return
    const refresh = window.setTimeout(
      () => {
        void store.refresh().catch(() => {})
      },
      Math.max(1000, state.expiresAt - Date.now() - 30000),
    )
    const expiry = window.setTimeout(
      store.expire,
      Math.max(0, state.expiresAt - Date.now()),
    )
    return () => {
      window.clearTimeout(refresh)
      window.clearTimeout(expiry)
    }
  }, [state.status, state.expiresAt, store])

  const refreshSession = useCallback(async () => {
    const status = store.getSnapshot().status
    if (status === 'better-auth') return store.refresh()
    if (status !== 'auth0') return false
    try {
      const token = await auth0.getAccessTokenSilently({ ignoreCache: true })
      store.setError(undefined)
      return Boolean(token)
    } catch (error) {
      store.setError(asError(error))
      throw asError(error)
    }
  }, [auth0, store])

  const getToken = useCallback(async () => {
    const status = store.getSnapshot().status
    if (status === 'better-auth') return store.getToken()
    if (status !== 'auth0') throw new Error('Please log in again.')
    // Auth0 owns its session lifecycle. Transient token failures must not sign it out.
    return auth0.getAccessTokenSilently({ ignoreCache: false })
  }, [auth0, store])

  const login = useCallback(
    async (options: RedirectLoginOptions) => {
      store.select('auth0')
      await auth0.loginWithRedirect(options)
    },
    [auth0, store],
  )

  const logout = useCallback(async () => {
    const status = store.getSnapshot().status
    try {
      if (status === 'better-auth') {
        const { error } = await getAuthClient().signOut()
        if (error)
          throw new Error(
            error.message || 'Could not log out. Please try again.',
          )
      } else if (status === 'auth0') {
        await auth0.logout({ returnTo: window.location.origin })
        // Auth0 keeps its authenticated state until the redirect unloads this page.
        // Signing out locally now would race that redirect with AppEntry's login.
        return
      }
      store.select('signed-out')
    } catch (error) {
      store.setError(asError(error))
      throw asError(error)
    }
  }, [auth0, store])

  return (
    <AuthContext.Provider
      value={{
        provider,
        user: isAuth0 ? (auth0.user as PortalAuthUser | undefined) : state.user,
        isAuthenticated: isAuth0
          ? auth0.isAuthenticated
          : Boolean(state.token && state.user),
        isLoading: isAuth0 ? auth0.isLoading : state.isLoading,
        error: state.error || (isAuth0 ? auth0.error : undefined),
        supportsAuth0AccountManagement:
          isAuth0 || Boolean(state.supportsAuth0AccountManagement),
        getToken,
        logout,
        refreshSession,
        login,
        selectProvider: store.select,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function usePortalAuth() {
  const auth = useContext(AuthContext)
  if (!auth) throw new Error('PortalAuthProvider is missing.')
  return auth
}
